import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../Components/ui/card';
import { Button } from '../Components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

import { 
  Mail, 
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Settings,
  Truck,
  Smartphone
} from 'lucide-react';

const faqCategoryDefinitions = [
  { id: 'general', icon: HelpCircle },
  { id: 'setup', icon: Settings },
  { id: 'shipping', icon: Truck },
  { id: 'support', icon: Smartphone },
];

const supportOptions = [{ icon: Mail, color: 'blue' }];

export default function HelpCenter() {
  const [expandedItems, setExpandedItems] = useState({});
  const navigate = useNavigate();
  const { t } = useLanguage();
  const copy = t('helpCenter');

  const toggleExpanded = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleContactNavigation = () => {
    navigate('/contact');
    // 确保页面滚动到顶部
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Hero Section */}
      <section id="help-center-hero" className="py-20 bg-gradient-to-br from-emerald-500 via-teal-600 to-green-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-teal-400/20"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-slide-in-up drop-shadow-lg">
              {t("interface.helpTitle")}
            </h1>
            <p className="text-lg text-emerald-100 animate-fade-in font-medium">
              {copy.hero.body}
            </p>
          </div>
        </div>
      </section>



      {/* Support Options */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {copy.support.title}
            </h2>
            <p className="text-lg text-gray-600">
              {copy.support.body}
            </p>
          </div>
          
          <div className="flex justify-center">
            <div className="max-w-md w-full">
            {supportOptions.map((option, index) => (
              <Card key={index} className="border-0 shadow-lg hover-lift text-center">
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-${option.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <option.icon className={`w-8 h-8 text-${option.color}-600`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {copy.support.optionTitle}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {copy.support.optionDescription}
                  </p>
                  <Button 
                    className={`bg-${option.color}-600 hover:bg-${option.color}-700 text-white`}
                    onClick={handleContactNavigation}
                  >
                    {copy.support.optionAction}
                  </Button>
                </CardContent>
              </Card>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {copy.faq.title}
            </h2>
            <p className="text-lg text-gray-600">
              {copy.faq.body}
            </p>
          </div>
          
          <div className="space-y-8">
            {faqCategoryDefinitions.map((category) => {
              const categoryCopy = copy.categories[category.id];
              return (
              <Card key={category.id} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <category.icon className="w-6 h-6 text-emerald-600 mr-3" />
                    {categoryCopy.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {categoryCopy.questions.map(([question, answer], index) => {
                      const isExpanded = expandedItems[`${category.id}-${index}`];
                      return (
                        <div key={index} className="border-b border-gray-100 last:border-b-0 pb-4 last:pb-0">
                          <button
                            onClick={() => toggleExpanded(category.id, index)}
                            className="flex items-center justify-between w-full text-left p-4 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <span className="font-medium text-gray-900 pr-4">
                              {question}
                            </span>
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            )}
                          </button>
                          {isExpanded && (
                            <div className="px-4 pb-4">
                              <p className="text-gray-600 leading-relaxed">
                                {answer}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            {copy.cta.title}
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            {copy.cta.body}
          </p>
          <div className="flex justify-center">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-gray-100"
              onClick={handleContactNavigation}
            >
              <Mail className="w-5 h-5 mr-2" />
              {copy.cta.action}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
